// src/pages/expense/_shared/ExpenseDraftList.jsx
// ✅ 전 사업 확산 [1] 공통화 — 환경/종합 파일럿의 목록 페이지(ExpenseEnvTotalDraftList.jsx)
// 를 그대로 일반화한 것. 지출 데이터 기준 목록에 기안 상태 컬럼을 더한 버전 — (구)
// 페이지로 입력한 건도 데이터 소스가 동일해 그대로 표시되며, 기안이 없는 건은
// "기안없음"으로 나온다. 사업별 차이(API 경로/작성 페이지 라우트/제목)는 config로 받는다.
// ✅ 공통 지출목록 스타일 정비 — 기안서 관리(draft_document/DraftDocumentList.jsx)
// 디자인을 그대로 재사용(ExpenseDraftList.module.css가 그쪽 클래스 정의를 복제).
// 공통 컴포넌트라 이 파일 하나를 고치면 8개 사업 지출목록에 전부 일괄 적용된다.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../api";
import { useTabActivated } from "../../../tabs/TabActivation";
import ExpenseDraftDetailModal from "./ExpenseDraftDetailModal";
import styles from "./ExpenseDraftList.module.css";

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "전체" },
  { value: "draft", label: "작성중" },
  { value: "pending", label: "결재중" },
  { value: "approved", label: "결재완료" },
  { value: "rejected", label: "반려" },
  { value: "none", label: "기안없음" },
];

// ✅ 기안 상태 배지 — 기안없음/작성중(회색)·결재중(노랑)·결재완료(초록)·반려(빨강).
// DraftDocumentList.jsx의 getStatusBadge와 동일한 매핑 방식(색은 badgeNone/
// badgePending/badgeApproved/badgeRejected로 재사용).
function statusBadgeInfo(row) {
  if (!row.draft_document_id) return { text: "기안없음", cls: styles.badgeNone };
  const map = {
    draft: { text: "작성중", cls: styles.badgeNone },
    pending: { text: "결재중", cls: styles.badgePending },
    approved: { text: "결재완료", cls: styles.badgeApproved },
    rejected: { text: "반려", cls: styles.badgeRejected },
  };
  return map[row.draft_status] || { text: row.draft_status || "-", cls: styles.badgeNone };
}

export default function ExpenseDraftList({ config }) {
  const { dataApiBase, newRoutePath, businessLabel, listRefreshChannelName } = config;
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  const [fromDate, setFromDate] = useState(`${year}-01-01`);
  const [toDate, setToDate] = useState(`${year}-12-31`);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [detailRow, setDetailRow] = useState(null);

  const loadList = async (searchVal) => {
    setLoading(true);
    try {
      const { data } = await api.get(dataApiBase, {
        params: { search: searchVal || undefined, ordering: "-draft_date,-seq" },
      });
      setRows(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      console.error("❌ 목록 불러오기 실패:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadList(search); }, [search]);

  // ✅ 공통 keep-alive 재방문 신호 — 저장 신호(아래)와 별개로, 이 탭을 다시 열 때마다
  // (구) 페이지 등 다른 경로로 데이터가 바뀌었을 수도 있으니 일반적으로도 재조회한다.
  useTabActivated(() => loadList(search));

  // ✅ keep-alive 탭 전환에서는 이 페이지가 마운트를 유지한 채 재사용되므로
  // (탭을 벗어났다 와도 위 useEffect가 다시 안 돈다) 통합 작성 페이지가 저장에
  // 성공하면 이 채널로 신호를 보내고, 여기서 받아서 직접 재조회한다.
  useEffect(() => {
    if (!("BroadcastChannel" in window)) return;
    const channel = new BroadcastChannel(listRefreshChannelName);
    channel.onmessage = (e) => {
      if (e.data?.type === "refresh") loadList(search);
    };
    return () => channel.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, listRefreshChannelName]);

  // ✅ 기간 필터만 적용한 목록 — 합계 집계는 이 기준(결재 상태 필터 무관, 현행과 동일 전건 합산)
  const inRange = useMemo(() => {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    to.setHours(23, 59, 59, 999);
    return rows.filter((r) => {
      const d = new Date(r.execution_date);
      return d >= from && d <= to;
    });
  }, [rows, fromDate, toDate]);

  // ✅ 화면 표시용 — 기간 필터 + 결재 상태 필터
  const displayRows = useMemo(() => {
    if (statusFilter === "all") return inRange;
    if (statusFilter === "none") return inRange.filter((r) => !r.draft_document_id);
    return inRange.filter((r) => r.draft_document_id && r.draft_status === statusFilter);
  }, [inRange, statusFilter]);

  const sums = useMemo(() => {
    let supply = 0, vat = 0;
    inRange.forEach((r) => { supply += Number(r.supply_amount) || 0; vat += Number(r.vat) || 0; });
    return { supply, vat, total: supply + vat };
  }, [inRange]);

  const getPartnerNames = (row) =>
    row.partners?.length ? row.partners.map((p) => p.name).filter(Boolean).join(", ") : row.partner_name || "-";

  async function handleDelete(row) {
    const hasDraft = !!row.draft_document_id;
    if (hasDraft && row.draft_status === "approved") {
      alert("결재완료 건은 삭제할 수 없습니다. (정책 확정 전까지 비활성화)");
      return;
    }
    const msg = hasDraft
      ? `기안(${row.draft_no})과 연결된 지출 데이터를 함께 삭제합니다.\n\n계속하시겠습니까?`
      : `삭제하시겠습니까?\n\n기안번호: ${row.draft_no}\n\n삭제 후 복구할 수 없습니다.`;
    if (!window.confirm(msg)) return;

    setDeletingId(row.id);
    try {
      if (hasDraft) {
        await api.delete(`/draft/documents/${row.draft_document_id}/`);
      } else {
        await api.delete(`${dataApiBase}${row.id}/`);
      }
      await loadList(search);
    } catch (e) {
      console.error(e);
      const msg2 = e.response?.data ? JSON.stringify(e.response.data).slice(0, 200) : e.message;
      alert(`삭제 실패: ${msg2}`);
    } finally {
      setDeletingId(null);
    }
  }

  if (loading) return <div className={styles.loading}>불러오는 중…</div>;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>🧾 {businessLabel} 지출목록</h2>
        <button className={styles.createBtn} onClick={() => navigate(newRoutePath)}>
          ➕ 지출데이터+기안 작성
        </button>
      </div>

      {/* 필터 영역 — 기간/상태/검색을 기안서 관리와 동일하게 한 줄 가로 배치,
          인풋 폭은 내용 크기 기반(검색어만 flex:1로 남는 폭을 채움). */}
      <div className={styles.filters}>
        <div className={styles.filterRow}>
          <span className={styles.filterLabel}>기간</span>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={styles.filterDate} />
          <span className={styles.filterLabel}>~</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={styles.filterDate} />

          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={styles.filterSelect}>
            {STATUS_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <input
            placeholder="기안번호/거래처/제목 검색"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && setSearch(searchInput.trim())}
            className={styles.filterSearch}
          />
          <button onClick={() => setSearch(searchInput.trim())} className={styles.searchBtn}>검색</button>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>작성일</th><th>기안번호</th><th>거래처</th><th>제목</th>
              <th>총금액</th><th>기안상태</th><th>관리</th>
            </tr>
          </thead>
          <tbody>
            <tr className={styles.sumRow}>
              <td colSpan={4}>합계(기간 전체, 결재상태 필터 무관)</td>
              <td className={styles.amount}>{sums.total.toLocaleString()}</td>
              <td colSpan={2}></td>
            </tr>
            {displayRows.map((r) => {
              const total = (Number(r.supply_amount) || 0) + (Number(r.vat) || 0);
              const approved = r.draft_document_id && r.draft_status === "approved";
              const badge = statusBadgeInfo(r);
              return (
                <tr key={r.id}>
                  <td>{r.execution_date}</td>
                  <td className={styles.draftNo}>{r.draft_no}</td>
                  <td className={styles.vendor}>{getPartnerNames(r)}</td>
                  <td className={styles.title}>{r.title}</td>
                  <td className={styles.amount}>{total.toLocaleString()}</td>
                  <td>
                    <span className={`${styles.badge} ${badge.cls}`}>{badge.text}</span>
                  </td>
                  <td>
                    <div className={styles.actionGroup}>
                      <button className={styles.viewBtn} onClick={() => setDetailRow(r)} title="상세보기">🔍</button>
                      <button
                        className={`${styles.deleteBtn} ${approved ? styles.disabledBtn : ""}`}
                        onClick={() => handleDelete(r)}
                        disabled={deletingId === r.id || approved}
                        title={approved ? "결재완료 건은 삭제할 수 없습니다" : "삭제"}
                      >
                        {deletingId === r.id ? "⏳" : "🗑️"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detailRow && (
        <ExpenseDraftDetailModal
          config={config}
          row={detailRow}
          onClose={() => setDetailRow(null)}
          onDeleted={() => loadList(search)}
        />
      )}
    </div>
  );
}
